import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { TransactionFormServiceError } from '../../../create-request/models/form/TransactionForm';
import { selectStateCode } from '../../../create-request/reducers';
import { selectSharedServiceErrors } from '../../reducers';
import { ActionCenterRedirectService } from '../../services/ActionCenterRedirect.service';
import { Hyperlink } from '../../types/Hyperlink';
import { ApiEndpointType, BannerTypes, Constants, navRouteConstants } from '../../utilities/Constants';
import { selectIsIra, selectMasterAccount } from '../../../account-info-header/reducers';
import { DrupalFormService } from '../../services/DrupalForm.service';
import { displayCancelModal } from '../../actions/shared.actions';

@Component({
  standalone: false,
  selector: 'mm-banners',
  templateUrl: './banners.component.html',
  styleUrls: ['./banners.component.scss']
})
export class BannersComponent implements OnInit {
  @Input() titleText: string;
  @Input() shouldBoldTitleText: boolean;
  @Input() bannerText: string[] = [];
  @Input() hyperlink: Hyperlink | null;
  @Input() routeRedirect: Hyperlink | null;
  @Input() boldText: string | null;
  @Input() showCloseIcon: boolean = true;
  @Input() type: BannerTypes;
  @Input() achRedirect: boolean;
  @Input() verifyAttentionMessage: boolean;
  @Input() stateTaxAttentionRequiredMI: boolean;
  @Input() stateTaxAttentionRequiredCT: boolean;
  @Input() stateTaxAttentionRequiredMN: boolean;
  @Input() RothSimpleTaxAttentionRequired: boolean;
  @Input() charitableMessage: boolean;
  @Input() warningMessages: Set<string> = new Set<string>();;
  @Input() serviceError: TransactionFormServiceError;
  @Input() downloadCheckJournalRequestForm: boolean;
  @Input() positionsLotFailure: boolean;
  @Input() htmlStr: string[] = [];
  @Input() useBulletedListDisplay: boolean = false;
  @Input() nonIsoAddressFooter: string | null;
  @Input() isInRFromTableDuplicateDraftsEnabled: boolean = false;
  allServiceErrors: { [serviceCallName: string]: TransactionFormServiceError };
  alertClosed: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  stateCode: string = '';
  url: string = "http://www.michigan.gov/taxes";
  michiganSpecialStateMessage: string = Constants.michiganVerifyMessage;
  masterAccount: string;
  isIra: boolean;

  constructor(
    private store: Store,
    private actionCenterRedirectService: ActionCenterRedirectService,
    private drupalFormService: DrupalFormService
  ) { }

  ngOnInit(): void {
    this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe(stateCd => { this.stateCode = stateCd; });
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe(masterAccount => this.masterAccount = masterAccount);
    this.store.select(selectIsIra).pipe(takeUntil(this.destroy$)).subscribe(isIra => this.isIra = isIra);
  }

  ngOnChanges(): void {
    const status = this.serviceError?.status?.toString() ?? '';
    if (status.startsWith('4') || status.startsWith('5')) {
      if (this.serviceError?.url.includes("MoneyMovementExperience")) {
        if (status === '400' && (this.serviceError.name === 'WireVerify' ||  this.serviceError.name === 'WireVerifyV3')) {
          const errorList  = this.serviceError.errorList?.map(err => {
            if (err.detail === Constants.wiresRegTypeError) {
              return err.detail;
            }
            return err.title;
        }).join('\n');
          this.showError(errorList ?? this.serviceError.message);
        } else if (status === '400' && this.serviceError.name === 'InternalTransferAcknowledge') {
          const errorList  = this.serviceError.errorList?.map(err => err.title).join('\n');
          this.showError(errorList ?? this.serviceError.message);
        } else if (status == '400' && this.serviceError.name === ApiEndpointType.checkVerify && this.serviceError.message.includes('QCD')) {
          this.bannerText[0] = this.serviceError.message;
        } else if (status == '400' && this.serviceError.name === ApiEndpointType.checkVerify) {
          const errorList = this.serviceError.errorList?.map(err => err.detail).join('\n');
          this.showError(errorList ?? this.serviceError.message);
        } else if (status == '409' && this.isInRFromTableDuplicateDraftsEnabled){
          this.showError(Constants.initiateAndReleaseDuplicateDraftError);
        } else {
          this.showError(Constants.genericServiceError);
        }
      } else if (this.serviceError?.url.includes("MoneyMovementUseSloa")) {
        this.showError(Constants.genericServiceError);
      } else if (this.serviceError?.url.includes("MoneyMovementStatusviewExperience")) {
        this.showError(Constants.genericServiceError);
      } else if (this.serviceError?.url.includes("PTRS")) {
        this.showError(Constants.genericServiceError)
      } else {
        this.bannerText[0] = this.serviceError.message;
      }
    }
    else if (this.serviceError?.message) {
      this.bannerText[0] = Constants.genericServiceError;
    }
  }

  iconName(): string {
    switch (this.type) {
      case 'error':
        return 'error';
      case 'information':
        return 'info';
      case 'warning':
        return 'warning';
      case 'confirmation':
        return 'success-circle';
      default:
        return ''
    }
  }

  showError(errorMessage: string) {
    const allServiceErrorsSub = this.store.select(selectSharedServiceErrors).pipe(takeUntil(this.destroy$)).subscribe(serviceErrors => {
      this.allServiceErrors = serviceErrors;
    })

    this.bannerText[0] = `${errorMessage}`

    allServiceErrorsSub.unsubscribe();
  }

  onClose(): void {
    this.alertClosed = true;
  }

  browserPopup(url: string, height?: any, width?: any, windowName?: any): void {
    if (height === undefined) {
      height = 500;
    }
    if (width === undefined) {
      width = 700;
    }
    if (windowName === undefined) {
      windowName = 'myWindow';
    }
    window.open(url, windowName, 'width=' + width + ', height= ' + height + ', scrollbars=yes, resizable=yes');
  }

  redirect() {
    if (this.hyperlink?.isActionCenterHyperLink) {
      this.actionCenterRedirectService.redirectToActionCenter();
    } else {
      window.open(this.hyperlink?.url, this.hyperlink?.target);
    }
  }

  redirectRoute() {
    this.store.dispatch(displayCancelModal({
      nextUrlState: navRouteConstants.createTransaction,
      title: 'Leave this page?',
      content: ['Return to Move Money to verify your submissions'],
      greenButton: 'Continue',
      grayButton: 'Back',
      isQuicklinksNav: false,
      source: 'transactionReviewPage'
    }))
  }

  getDrupalPdf(name: string) {
    this.drupalFormService.createDrupalForm(name);
  }
}
